import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import api from '../lib/api';
import {
  getTaskStatusLabel,
  getTaskPriorityLabel,
  getTaskPriorityColor,
} from '../components/process/ProcessTaskListCard';

export const Route = createFileRoute('/client/processes/$processId/tasks')({
  component: ClientProcessTasksPage,
});

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'BACKLOG', label: 'Aguardando' },
  { value: 'IN_PROGRESS', label: 'Em Progresso' },
  { value: 'BLOCKED', label: 'Bloqueada' },
  { value: 'ON_HOLD', label: 'Em Espera' },
  { value: 'DONE', label: 'Concluída' },
  { value: 'CANCELED', label: 'Cancelada' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'Todas as prioridades' },
  { value: 'URGENT', label: 'Urgente' },
  { value: 'HIGH', label: 'Alta' },
  { value: 'MEDIUM', label: 'Média' },
  { value: 'LOW', label: 'Baixa' },
];

function formatDateTime(date: Date | string | null | undefined) {
  if (!date) return null;
  return new Date(date).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ClientProcessTasksPage() {
  const { processId } = Route.useParams();

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: tasks, isLoading, error } = useQuery<any[]>({
    queryKey: ['client-portal', 'process', processId, 'tasks', debouncedSearch, statusFilter, priorityFilter],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      const r = await api.get(`/client-portal/processes/${processId}/tasks`, { params });
      return r.data?.data ?? r.data;
    },
  });

  if (isLoading) {
    return (
      <div className="bg-white rounded-[15px] p-5 shadow-sm border border-gray-100">
        <p className="text-sm text-gray-500" style={{ fontFamily: 'Poppins, sans-serif' }}>
          Carregando tarefas...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-[15px] p-5 shadow-sm border border-gray-100">
        <p className="text-sm text-red-600" style={{ fontFamily: 'Poppins, sans-serif' }}>
          Erro ao carregar as tarefas.
        </p>
      </div>
    );
  }

  const hasFilters = !!debouncedSearch || !!statusFilter || !!priorityFilter;

  return (
    <div className="bg-white rounded-[15px] p-5 shadow-sm border border-gray-100" style={{ fontFamily: 'Poppins, sans-serif' }}>
      <h2 className="text-[14px] font-semibold text-[#06141B]">Tarefas</h2>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Buscar por nome..."
          className="px-3 py-2 rounded-[10px] border border-gray-200 text-sm bg-gray-50 w-full"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-[10px] border border-gray-200 text-sm bg-gray-50 w-full"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          className="px-3 py-2 rounded-[10px] border border-gray-200 text-sm bg-gray-50 w-full"
        >
          {PRIORITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {hasFilters && (
        <button
          type="button"
          onClick={() => { setSearchInput(''); setStatusFilter(''); setPriorityFilter(''); }}
          className="mt-2 text-[11px] text-gray-500 underline"
        >
          Limpar filtros
        </button>
      )}

      {!tasks?.length ? (
        <p className="text-sm text-gray-500 mt-4">
          {hasFilters ? 'Nenhuma tarefa encontrada para os filtros selecionados.' : 'Nenhuma tarefa encontrada.'}
        </p>
      ) : (
        <div className="mt-4 space-y-2">
          {tasks.map((t: any) => {
            const priorityLabel = getTaskPriorityLabel(t.priority);
            const priorityColor = getTaskPriorityColor(t.priority);
            return (
              <div key={t.id} className="p-4 rounded-[12px] border border-gray-100">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#06141B] truncate">{t.title}</p>
                    {t.description && (
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">{t.description}</p>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span
                        className="text-xs px-2.5 py-0.5 rounded-[10px] font-medium"
                        style={{
                          backgroundColor: `${priorityColor}18`,
                          color: priorityColor,
                          border: `1px solid ${priorityColor}40`,
                        }}
                      >
                        {priorityLabel ?? 'Sem prioridade'}
                      </span>
                      <div className="space-y-0.5 text-[11px] text-gray-600">
                        {t.securityDeadline ? (
                          <span>
                            <span className="font-medium text-gray-700">Seg.: </span>
                            {formatDateTime(t.securityDeadline)}
                          </span>
                        ) : null}
                        {t.dueDate && (!t.securityDeadline || t.dueDate !== t.securityDeadline) ? (
                          <span className="ml-2">
                            <span className="font-medium text-gray-700">Prazo: </span>
                            {formatDateTime(t.dueDate)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <span className="text-xs px-2.5 py-1 rounded-[10px] bg-gray-100 text-gray-700 whitespace-nowrap shrink-0">
                    {getTaskStatusLabel(t.status)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
